
import { Component, Ref, Vue, Prop } from "vue-property-decorator";
import { namespace } from "vuex-class";
import { PrefTypesEnum, PrefTypeRequest } from "@/store/settings/types";
import { AlarmSoundType } from "@/store/types";

const SettingsStore = namespace("settings");

const PrefsOfInterest = {
	AlarmAlertsSuppressFlashing: {
		groupID: -1,
		prefTypeID: PrefTypesEnum.AlarmAlertsSuppressFlashing
	} as PrefTypeRequest,
	AlarmAlertsSuppressAudio: {
		groupID: -1,
		prefTypeID: PrefTypesEnum.AlarmAlertsSuppressAudio
	} as PrefTypeRequest
};

@Component({})
export default class AlarmAlert extends Vue {
	@Prop({ type: Boolean, default: false }) suppressAlerts: boolean;
	@Prop({ type: Boolean, default: false }) suppressAudioAlerts: boolean;
	@Prop({ type: Boolean, default: false }) suppressVisualAlerts: boolean;
	@Prop({ type: String, default: AlarmSoundType.Alarm.toString() }) alarmType: AlarmSoundType;

	@SettingsStore.Action getPrefsFromList: (prefEnumList: PrefTypeRequest[]) => void;
	@SettingsStore.Getter getPrefBoolValue: (prefType: PrefTypeRequest) => boolean;

	@Ref() readonly alarmAlertAudio: HTMLAudioElement;

	// -- LifeCycle Hooks -- //
	// Ensure we get the latest prefs whenever this component mounts
	// #1 Without this, changing the pref in the database manually and then refreshing the browser does NOT update the collection of prefs.
	/*
            #1 : This behaviour is different when changed in the System Settings view; ONLY the user who changed the setting or users who have viewed the
            system settings view since the change was made would have the latest settings.

        */
	public async mounted() {
		await this.getPrefsFromList([
			PrefsOfInterest.AlarmAlertsSuppressFlashing,
			PrefsOfInterest.AlarmAlertsSuppressAudio
		]);
	}

	/**
	 * Trigger a visual alert
	 */
	private triggerVisualAlert(): void {
		document.body.classList.add("alarm-body");

		setTimeout(() => {
			if (document.body.classList.contains("alarm-body")) {
				document.body.classList.remove("alarm-body");
			}
		}, 1000);
	}

	/**
	 * Trigger and Auditory alert.
	 */
	private triggerAudioAlert(): void {
		try {
			this.alarmAlertAudio.play();
		} catch (err) {
			console.log(`Error playing alarm sound: ${err}`);
		}
	}

	/**
	 * Trigger an alert ; Will play an audio clip and toggle a red border momentarily.
	 * Will not trigger in the event that : @suppressAlerts is true.
	 * Visual alerts will not trigger in the event that @suppressVisualAlerts is true or the Pref to suppress visual alerts is enabled.
	 * Audio alerts will not trigger in the event that @suppressAudioAlerts is true or the Pref to suppress audio alerts is enabled.
	 */
	public triggerAlert(): void {
		// As long as alerts are not completely suppressed, continue.
		if (!this.suppressAlerts) {
			if (
				process.env.VUE_APP_ALARM_BLINKING_ENABLED &&
				!this.suppressVisualAlerts &&
				!this.getPrefBoolValue(PrefsOfInterest.AlarmAlertsSuppressFlashing)
			) {
				this.triggerVisualAlert();
			}

			if (
				process.env.VUE_APP_ALARM_AUDIO_ENABLED &&
				!this.suppressAudioAlerts &&
				!this.getPrefBoolValue(PrefsOfInterest.AlarmAlertsSuppressAudio)
			) {
				this.triggerAudioAlert();
			}
		}
	}

	private get alarmSourceFile() {
		return this.alarmType;
	}
}
